/**
 * XRWebGPUPresentationBackend — Experimental WebGPU XR presentation backend.
 *
 * Follows the WebXR-WebGPU Binding proposal:
 * - Creates XRGPUBinding(session, device)
 * - Creates XRProjectionLayer via binding
 * - Uses session.updateRenderState({ layers: [...] }) — NO baseLayer
 * - Per-view: gets sub-image color/depth textures from binding
 *
 * EXPERIMENTAL: Only renders a clear-color present pass for validation.
 * Full engine pipeline integration is deferred until Three.js supports it.
 */

import type { IXRPresentationBackend, XrViewRenderTarget, DepthRange } from './IXRPresentationBackend.js';
import { Logger } from '../../core/Logger.js';

export class XRWebGPUPresentationBackend implements IXRPresentationBackend {
  readonly binding = 'webgpu' as const;
  readonly depthRange: DepthRange = '0to1';
  readonly supportsLayers = true; // WebGPU XR always uses layers (no baseLayer)

  private _device: GPUDevice | null;
  private _gpuBinding: XRGPUBinding | null = null;
  private _projectionLayer: XRProjectionLayer | null = null;
  private _layerTypes = new Set<string>(['projection']);
  // Reusable buffers — XR typically has 2 views (left/right)
  private _viewMatrices: Float32Array[] = [new Float32Array(16), new Float32Array(16)];
  private _projMatrices: Float32Array[] = [new Float32Array(16), new Float32Array(16)];
  /**
   * Whether this backend owns the GPUDevice and should destroy it on detach.
   * `XRSessionNegotiator` allocates a new xrCompatible device per session, so
   * the default is true. If a future caller ever passes the renderer's own
   * device here, flip this to false in the constructor.
   */
  private _ownsDevice: boolean;

  constructor(device: GPUDevice, ownsDevice = true) {
    this._device = device;
    this._ownsDevice = ownsDevice;
  }

  get layerTypes(): Set<string> {
    return this._layerTypes;
  }

  async attachSession(session: XRSession): Promise<void> {
    try {
      // Create XRGPUBinding per the WebXR-WebGPU Binding proposal
      const GpuBindingCtor = (globalThis as any).XRGPUBinding;
      if (!GpuBindingCtor) {
        throw new Error('XRGPUBinding not available in this browser');
      }

      this._gpuBinding = new GpuBindingCtor(session, this._device);

      // Create projection layer via the GPU binding
      this._projectionLayer = this._gpuBinding!.createProjectionLayer({
        colorFormat: 'bgra8unorm',
        depthStencilFormat: 'depth24plus-stencil8',
        scaleFactor: 1.0,
      });

      // WebGPU XR uses layers array, NOT baseLayer
      session.updateRenderState({
        layers: [this._projectionLayer!],
      } as any);

      Logger.info('XRWebGPUBackend', 'Session attached (WebGPU binding, experimental)');
    } catch (err) {
      Logger.warn('XRWebGPUBackend', `Failed to create GPU binding: ${err}`);
      throw err;
    }
  }

  async detachSession(): Promise<void> {
    this._gpuBinding = null;
    this._projectionLayer = null;
    const device = this._device;
    // Null first so a re-entrant detach (e.g. session-end firing twice
    // from the browser) skips the destroy path. Calling destroy() on an
    // already-destroyed GPUDevice throws in some implementations and
    // silently corrupts queues in others.
    this._device = null;
    if (!device) {
      Logger.info('XRWebGPUBackend', 'Session detached (device already released)');
      return;
    }
    // Wait for in-flight GPU work to complete before destroying the device.
    // This prevents the compositor from reading a destroyed device's buffers.
    try {
      await device.queue?.onSubmittedWorkDone?.();
    } catch {
      // Device may already be lost — safe to ignore
    }
    if (this._ownsDevice) {
      device.destroy?.();
    }
    Logger.info('XRWebGPUBackend', 'Session detached');
  }

  beginFrame(frame: XRFrame, refSpace: XRReferenceSpace): { views: XrViewRenderTarget[] } {
    const pose = frame.getViewerPose(refSpace);
    if (!pose) return { views: [] };

    if (!this._gpuBinding || !this._projectionLayer) return { views: [] };

    const views: XrViewRenderTarget[] = [];

    for (const view of pose.views) {
      let subImage: XRGPUSubImage;
      try {
        subImage = this._gpuBinding.getViewSubImage(this._projectionLayer, view);
      } catch {
        continue; // Skip this view if sub-image retrieval fails
      }

      const viewport = {
        x: subImage.viewport.x,
        y: subImage.viewport.y,
        width: subImage.viewport.width,
        height: subImage.viewport.height,
      };

      // Derive view matrix from XRView.transform.inverse — reuse buffers
      const viewIdx = views.length;
      if (viewIdx >= this._viewMatrices.length) {
        this._viewMatrices.push(new Float32Array(16));
        this._projMatrices.push(new Float32Array(16));
      }
      const viewMatrix = this._viewMatrices[viewIdx];
      const projMatrix = this._projMatrices[viewIdx];
      viewMatrix.set(view.transform.inverse.matrix);
      projMatrix.set(view.projectionMatrix);

      views.push({
        view,
        viewport,
        projectionMatrix: projMatrix,
        viewMatrix,
        depthRange: this.depthRange,
        color: subImage.colorTexture,
        depth: subImage.depthStencilTexture,
      });
    }

    // Minimal present pass: clear each view to a distinct color
    this._presentClearPass(views);

    return { views };
  }

  /**
   * Minimal present pass — clears each view's color texture.
   * Left eye: cyan (0.0, 0.8, 1.0), Right eye: magenta (1.0, 0.0, 0.8)
   * Uses a single command encoder for all views to reduce GPU overhead.
   */
  private _presentClearPass(views: XrViewRenderTarget[]): void {
    const device = this._device;
    if (!device) return;
    try {
      const encoder = device.createCommandEncoder();

      for (let i = 0; i < views.length; i++) {
        const colorTexture = views[i].color as GPUTexture;
        if (!colorTexture) continue;

        const colorView = colorTexture.createView();
        const clearColor = i === 0
          ? { r: 0.0, g: 0.8, b: 1.0, a: 1.0 }  // Left eye: cyan
          : { r: 1.0, g: 0.0, b: 0.8, a: 1.0 };  // Right eye: magenta

        const passDesc: GPURenderPassDescriptor = {
          colorAttachments: [{
            view: colorView,
            loadOp: 'clear',
            storeOp: 'store',
            clearValue: clearColor,
          }],
        };

        const renderPass = encoder.beginRenderPass(passDesc);
        renderPass.end();
      }

      device.queue.submit([encoder.finish()]);
    } catch {
      // Silently skip if clear fails (experimental path)
    }
  }
}
